import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  CalendarCheck,
  CheckCircle2,
  FileUp,
  Loader2,
  Plus,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { ErrorSummary, Field, YesNo } from "./FormControls";
import {
  ACCEPTED_DOCUMENT_TYPES,
  defaultPhoenixSpaces,
  phoenixHubContent,
} from "./config";
import {
  DAY_OPTIONS,
  calculateIndicativeEstimate,
  getLondonTodayISO,
  initialBookingForm,
  needsSafeguardingDetails,
  transformBookingForm,
  validateBookingStep,
  validateCompleteBooking,
} from "./bookingUtils";
import {
  checkPhoenixAvailability,
  loadPhoenixSpaces,
  submitPhoenixBooking,
} from "./service";
import PhoenixBookingReview from "./PhoenixBookingReview";
import "./PhoenixHub.css";
import "./PhoenixBookingForm.css";

const DRAFT_KEY = "uun_phoenix_booking_draft_v1";
const SUBMISSION_KEY = "uun_phoenix_submission_key_v1";
const STEP_TITLES = [
  "Contact details",
  "Booking type",
  "Space selection",
  "Date and time",
  "Activity details",
  "Compliance and safeguarding",
  "Declaration",
];

function PolicyReference({ href, children }) {
  return href ? <a href={href}>{children}</a> : <span>{children}</span>;
}

function loadDraft() {
  try {
    const value = JSON.parse(sessionStorage.getItem(DRAFT_KEY));
    return value && typeof value === "object"
      ? {
          ...initialBookingForm,
          ...value,
          contact: { ...initialBookingForm.contact, ...value.contact },
          oneOff: { ...initialBookingForm.oneOff, ...value.oneOff },
          recurrence: { ...initialBookingForm.recurrence, ...value.recurrence },
          activity: { ...initialBookingForm.activity, ...value.activity },
          compliance: { ...initialBookingForm.compliance, ...value.compliance },
          declarations: { ...initialBookingForm.declarations, ...value.declarations },
        }
      : initialBookingForm;
  } catch {
    return initialBookingForm;
  }
}

function getSubmissionKey() {
  const existing = sessionStorage.getItem(SUBMISSION_KEY);
  if (existing) return existing;
  const next = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  sessionStorage.setItem(SUBMISSION_KEY, next);
  return next;
}

export default function PhoenixBookingForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(loadDraft);
  const [step, setStep] = useState(1);
  const [spaces, setSpaces] = useState(defaultPhoenixSpaces);
  const [errors, setErrors] = useState({});
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [availability, setAvailability] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const today = getLondonTodayISO();

  useEffect(() => {
    loadPhoenixSpaces().then(setSpaces);
  }, []);

  useEffect(() => {
    const requestedSpace = searchParams.get("space");
    if (!requestedSpace) return;
    setForm((current) =>
      current.selectedSpaceIds.length
        ? current
        : { ...current, selectedSpaceIds: [requestedSpace] }
    );
  }, [searchParams]);

  useEffect(() => {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ ...form, honeypot: "" }));
  }, [form]);

  const estimate = useMemo(() => calculateIndicativeEstimate(form, spaces), [form, spaces]);
  const safeguardingDetailsRequired = needsSafeguardingDetails(form);

  const updateSection = (section, key, value) => {
    setForm((current) => ({
      ...current,
      [section]: { ...current[section], [key]: value },
    }));
    setErrors((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const updateSafeguardingTrigger = (section, key, value) => {
    setForm((current) => {
      const next = {
        ...current,
        [section]: { ...current[section], [key]: value },
      };
      if (!needsSafeguardingDetails(next)) {
        next.compliance = {
          ...next.compliance,
          safeguardingPolicy: "",
          dbsRequired: "",
        };
      }
      return next;
    });
    setErrors((current) => {
      const next = { ...current };
      [key, "safeguardingPolicy", "dbsRequired"].forEach((errorKey) => delete next[errorKey]);
      return next;
    });
  };

  const toggleSpace = (spaceId) => {
    setAvailability(null);
    setForm((current) => {
      if (spaceId === "whole-centre" || spaceId === "unsure") {
        return {
          ...current,
          selectedSpaceIds:
            current.selectedSpaceIds.length === 1 && current.selectedSpaceIds[0] === spaceId
              ? []
              : [spaceId],
        };
      }
      const withoutExclusive = current.selectedSpaceIds.filter(
        (id) => !["whole-centre", "unsure"].includes(id)
      );
      return {
        ...current,
        selectedSpaceIds: withoutExclusive.includes(spaceId)
          ? withoutExclusive.filter((id) => id !== spaceId)
          : [...withoutExclusive, spaceId],
      };
    });
  };

  const validateAndContinue = () => {
    const nextErrors = validateBookingStep(step, form, today);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      requestAnimationFrame(() => document.querySelector(".phx-error-summary")?.focus());
      return;
    }
    setStep((current) => Math.min(7, current + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const addMultiDay = () => {
    setForm((current) => ({
      ...current,
      multiDayDates: [...current.multiDayDates, { date: "", startTime: "", endTime: "" }],
    }));
  };

  const updateMultiDay = (index, key, value) => {
    setAvailability(null);
    setForm((current) => ({
      ...current,
      multiDayDates: current.multiDayDates.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [key]: value } : item
      ),
    }));
  };

  const removeMultiDay = (index) => {
    setForm((current) => ({
      ...current,
      multiDayDates: current.multiDayDates.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const toggleRecurringDay = (day) => {
    setForm((current) => ({
      ...current,
      recurrence: {
        ...current.recurrence,
        daysOfWeek: current.recurrence.daysOfWeek.includes(day)
          ? current.recurrence.daysOfWeek.filter((value) => value !== day)
          : [...current.recurrence.daysOfWeek, day],
      },
    }));
  };

  const handleFiles = (event) => {
    const selected = Array.from(event.target.files || []);
    setFileError("");
    if (selected.length > 6) {
      setFileError("Upload no more than six documents.");
      return;
    }
    const invalid = selected.find(
      (file) =>
        !Object.keys(ACCEPTED_DOCUMENT_TYPES).includes(file.type) ||
        file.size > phoenixHubContent.maxUploadBytes
    );
    if (invalid) {
      setFileError(`${invalid.name} is not an accepted PDF, DOCX, JPG or PNG under 10 MB.`);
      return;
    }
    setFiles(selected);
  };

  const handleAvailabilityCheck = async () => {
    const dateErrors = validateBookingStep(4, form, today);
    const spaceErrors = validateBookingStep(3, form, today);
    if (Object.keys({ ...dateErrors, ...spaceErrors }).length) {
      setErrors({ ...spaceErrors, ...dateErrors });
      return;
    }
    setCheckingAvailability(true);
    setAvailability(null);
    try {
      const payload = transformBookingForm(form, spaces);
      setAvailability(await checkPhoenixAvailability(payload));
    } catch {
      setAvailability({
        status: "contact_us",
        message: "We could not check live availability. You can still submit a request for the UUN team to review.",
      });
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validateCompleteBooking(form, today);
    setErrors(nextErrors);
    setSubmitError("");
    if (Object.keys(nextErrors).length) {
      setSubmitError("Some required information is missing. Use the step links above to review the form.");
      requestAnimationFrame(() => document.querySelector(".phx-error-summary")?.focus());
      return;
    }
    setSubmitting(true);
    try {
      const payload = transformBookingForm(form, spaces);
      const result = await submitPhoenixBooking(payload, files, getSubmissionKey());
      sessionStorage.removeItem(DRAFT_KEY);
      sessionStorage.removeItem(SUBMISSION_KEY);
      sessionStorage.setItem("uun_phoenix_last_submission", JSON.stringify(result));
      navigate(`/phoenix-hub/booking-success?reference=${encodeURIComponent(result.bookingReference)}`, {
        replace: true,
        state: result,
      });
    } catch (error) {
      setSubmitError(
        error?.message?.replace(/^FirebaseError:\s*/i, "") ||
          "Your request could not be submitted. Your information is still on this page; check your connection and try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="phx-shell phx-booking-shell">
      <Link className="phx-back-link" to="/phoenix-hub"><ArrowLeft aria-hidden="true" /> Phoenix Hub</Link>
      <header className="phx-page-head phx-form-head">
        <p className="phx-kicker">Request, review, approve</p>
        <h1>Request a booking</h1>
        <p>Tell us about your activity. You can review each section before sending your request.</p>
      </header>

      <aside className="phx-notice" role="note"><CalendarCheck aria-hidden="true" /><div><h2>This is a request, not a confirmed booking</h2><p>{phoenixHubContent.bookingNotice}</p></div></aside>

      <nav className="phx-stepper" aria-label="Booking form progress">
        {STEP_TITLES.map((title, index) => {
          const number = index + 1;
          return (
            <button
              key={title}
              type="button"
              className={number === step ? "active" : number < step ? "complete" : ""}
              onClick={() => number < step && setStep(number)}
              aria-current={number === step ? "step" : undefined}
            >
              <span>{number < step ? <CheckCircle2 aria-hidden="true" /> : number}</span>
              <small>{title}</small>
            </button>
          );
        })}
      </nav>

      <form className="phx-booking-form" onSubmit={handleSubmit} noValidate>
        <ErrorSummary errors={errors} />
        <section className="phx-form-section" aria-labelledby={`step-title-${step}`}>
          <div className="phx-form-section-title">
            <p className="phx-kicker">Step {step} of 7</p>
            <h2 id={`step-title-${step}`}>{STEP_TITLES[step - 1]}</h2>
          </div>

          {step === 1 ? (
            <div className="phx-form-grid">
              <Field label="Full name *" error={errors.fullName}><input value={form.contact.fullName} onChange={(e) => updateSection("contact", "fullName", e.target.value)} autoComplete="name" maxLength="120" /></Field>
              <Field label="Organisation or group name (optional)"><input value={form.contact.organisationName} onChange={(e) => updateSection("contact", "organisationName", e.target.value)} autoComplete="organization" maxLength="160" /></Field>
              <Field label="Email address *" error={errors.email}><input type="email" value={form.contact.email} onChange={(e) => updateSection("contact", "email", e.target.value)} autoComplete="email" maxLength="160" /></Field>
              <Field label="Telephone number *" error={errors.telephone}><input type="tel" value={form.contact.telephone} onChange={(e) => updateSection("contact", "telephone", e.target.value)} autoComplete="tel" maxLength="40" /></Field>
              <Field label="Preferred contact method *" error={errors.preferredContactMethod}><select value={form.contact.preferredContactMethod} onChange={(e) => updateSection("contact", "preferredContactMethod", e.target.value)}><option value="email">Email</option><option value="telephone">Telephone</option><option value="either">Either</option></select></Field>
              <Field label="Address or postcode (optional)"><input value={form.contact.postcode} onChange={(e) => updateSection("contact", "postcode", e.target.value)} autoComplete="postal-code" maxLength="160" /></Field>
            </div>
          ) : null}

          {step === 2 ? (
            <fieldset className="phx-card-options"><legend className="phx-sr-only">Booking type</legend>{[
              ["one_off", "One-off booking", "A single date and time."],
              ["recurring", "Recurring booking", "A regular class, club or programme."],
              ["multi_day", "Multi-day booking", "Several specific dates and time ranges."],
              ["general_enquiry", "General availability enquiry", "Ask about options before choosing dates."],
            ].map(([value, label, description]) => <label key={value}><input type="radio" name="bookingType" value={value} checked={form.bookingType === value} onChange={(e) => setForm((current) => ({ ...current, bookingType: e.target.value }))} /><span><strong>{label}</strong><small>{description}</small></span></label>)}</fieldset>
          ) : null}

          {step === 3 ? (
            <fieldset><legend className="phx-sr-only">Select spaces</legend><div className="phx-card-options phx-space-options">{[...spaces, { id: "unsure", name: "Unsure — please advise", description: "Tell us about your activity and UUN will recommend a space.", rateLabel: "We’ll help you choose" }].map((space) => <label key={space.id}><input type="checkbox" checked={form.selectedSpaceIds.includes(space.id)} onChange={() => toggleSpace(space.id)} /><span><strong>{space.name}</strong><small>{space.description}</small><em>{space.rateLabel}</em></span></label>)}</div>{errors.selectedSpaceIds ? <strong className="phx-inline-error" role="alert">{errors.selectedSpaceIds}</strong> : null}</fieldset>
          ) : null}

          {step === 4 && form.bookingType === "one_off" ? (
            <div className="phx-form-grid">
              <Field label="Requested date *" error={errors.date}><input type="date" min={today} value={form.oneOff.date} onChange={(e) => { updateSection("oneOff", "date", e.target.value); setAvailability(null); }} /></Field>
              <Field label="Start time *" error={errors.time}><input type="time" value={form.oneOff.startTime} onChange={(e) => { updateSection("oneOff", "startTime", e.target.value); setAvailability(null); }} /></Field>
              <Field label="End time *" error={errors.time}><input type="time" value={form.oneOff.endTime} onChange={(e) => { updateSection("oneOff", "endTime", e.target.value); setAvailability(null); }} /></Field>
              <div className="phx-form-divider"><span>Optional alternative</span></div>
              <Field label="Alternative date" error={errors.alternativeDate}><input type="date" min={today} value={form.oneOff.alternativeDate} onChange={(e) => updateSection("oneOff", "alternativeDate", e.target.value)} /></Field>
              <Field label="Alternative start" error={errors.alternativeTime}><input type="time" value={form.oneOff.alternativeStartTime} onChange={(e) => updateSection("oneOff", "alternativeStartTime", e.target.value)} /></Field>
              <Field label="Alternative end" error={errors.alternativeTime}><input type="time" value={form.oneOff.alternativeEndTime} onChange={(e) => updateSection("oneOff", "alternativeEndTime", e.target.value)} /></Field>
            </div>
          ) : null}

          {step === 4 && form.bookingType === "recurring" ? (
            <div className="phx-form-grid">
              <Field label="Preferred start date *" error={errors.startDate}><input type="date" min={today} value={form.recurrence.startDate} onChange={(e) => updateSection("recurrence", "startDate", e.target.value)} /></Field>
              <Field label="Preferred end date *" error={errors.endDate}><input type="date" min={form.recurrence.startDate || today} value={form.recurrence.endDate} onChange={(e) => updateSection("recurrence", "endDate", e.target.value)} /></Field>
              <fieldset className="phx-choice-field phx-full"><legend>Days of the week *</legend><div className="phx-day-options">{DAY_OPTIONS.map(([key, label]) => <label key={key}><input type="checkbox" checked={form.recurrence.daysOfWeek.includes(key)} onChange={() => toggleRecurringDay(key)} />{label.slice(0, 3)}</label>)}</div>{errors.daysOfWeek ? <strong role="alert">{errors.daysOfWeek}</strong> : null}</fieldset>
              <Field label="Start time *" error={errors.recurrenceTime}><input type="time" value={form.recurrence.startTime} onChange={(e) => updateSection("recurrence", "startTime", e.target.value)} /></Field>
              <Field label="End time *" error={errors.recurrenceTime}><input type="time" value={form.recurrence.endTime} onChange={(e) => updateSection("recurrence", "endTime", e.target.value)} /></Field>
              <Field label="Frequency *"><select value={form.recurrence.frequency} onChange={(e) => updateSection("recurrence", "frequency", e.target.value)}><option value="weekly">Weekly</option><option value="fortnightly">Fortnightly</option><option value="monthly">Monthly</option><option value="custom">Custom</option></select></Field>
              <Field label="Expected number of sessions *" error={errors.expectedSessions}><input type="number" min="1" max="104" value={form.recurrence.expectedSessions} onChange={(e) => updateSection("recurrence", "expectedSessions", e.target.value)} /></Field>
              {form.recurrence.frequency === "custom" ? <Field label="Describe the custom pattern *" error={errors.customPattern} className="phx-full"><textarea rows="3" value={form.recurrence.customPattern} onChange={(e) => updateSection("recurrence", "customPattern", e.target.value)} maxLength="500" /></Field> : null}
              <Field label="Dates to exclude (optional)" hint="Enter dates as YYYY-MM-DD, separated by commas." className="phx-full"><input value={form.recurrence.excludedDates} onChange={(e) => updateSection("recurrence", "excludedDates", e.target.value)} placeholder="2030-08-10, 2030-08-17" /></Field>
            </div>
          ) : null}

          {step === 4 && form.bookingType === "multi_day" ? (
            <div className="phx-multi-days">{form.multiDayDates.map((item, index) => <div className="phx-multi-day" key={`date-${index}`}><strong>Date {index + 1}</strong><Field label="Date" error={errors[`multiDay-${index}`]}><input type="date" min={today} value={item.date} onChange={(e) => updateMultiDay(index, "date", e.target.value)} /></Field><Field label="Start" error={errors[`multiDayTime-${index}`]}><input type="time" value={item.startTime} onChange={(e) => updateMultiDay(index, "startTime", e.target.value)} /></Field><Field label="End" error={errors[`multiDayTime-${index}`]}><input type="time" value={item.endTime} onChange={(e) => updateMultiDay(index, "endTime", e.target.value)} /></Field>{form.multiDayDates.length > 1 ? <button type="button" className="phx-icon-button" onClick={() => removeMultiDay(index)} aria-label={`Remove date ${index + 1}`}><Trash2 aria-hidden="true" /></button> : null}</div>)}<button className="phx-button phx-button-secondary" type="button" onClick={addMultiDay}><Plus aria-hidden="true" /> Add another date</button></div>
          ) : null}

          {step === 4 && form.bookingType === "general_enquiry" ? <div className="phx-soft-card"><CalendarCheck aria-hidden="true" /><div><h3>No date is needed yet</h3><p>Use the remaining sections to tell UUN what you are planning. The team will discuss suitable dates with you.</p></div></div> : null}

          {step === 4 && form.bookingType !== "general_enquiry" ? <div className="phx-availability-check"><button type="button" className="phx-button phx-button-secondary" onClick={handleAvailabilityCheck} disabled={checkingAvailability}>{checkingAvailability ? <Loader2 className="phx-spin" aria-hidden="true" /> : <CalendarCheck aria-hidden="true" />} Check indicative availability</button>{availability ? <p className={`phx-availability-${availability.status}`} role="status"><strong>{availability.status === "available" ? "Available" : availability.status === "limited" ? "Limited availability" : "Contact us"}:</strong> {availability.message}</p> : null}</div> : null}

          {step === 5 ? (
            <div className="phx-form-grid">
              <Field label="Activity or event name *" error={errors.activityName}><input value={form.activity.name} onChange={(e) => updateSection("activity", "name", e.target.value)} maxLength="160" /></Field>
              <Field label="Expected number of attendees *" error={errors.expectedAttendees}><input type="number" min="1" max="1000" value={form.activity.expectedAttendees} onChange={(e) => updateSection("activity", "expectedAttendees", e.target.value)} /></Field>
              <Field label="Description of the proposed activity *" error={errors.activityDescription} className="phx-full"><textarea rows="5" value={form.activity.description} onChange={(e) => updateSection("activity", "description", e.target.value)} maxLength="4000" /></Field>
              <Field label="Age range of attendees *" error={errors.attendeeAgeRange}><input value={form.activity.attendeeAgeRange} onChange={(e) => updateSection("activity", "attendeeAgeRange", e.target.value)} placeholder="For example: all ages, 8–12, adults" maxLength="80" /></Field>
              <Field label="Who may attend? *"><select value={form.activity.attendanceType} onChange={(e) => updateSection("activity", "attendanceType", e.target.value)}><option value="public">Open to the public</option><option value="private">Private</option><option value="invitation_only">Invitation only</option></select></Field>
              <YesNo label="Will children or vulnerable adults attend? *" name="childrenOrVulnerableAdults" value={form.activity.childrenOrVulnerableAdults} onChange={(value) => updateSafeguardingTrigger("activity", "childrenOrVulnerableAdults", value)} error={errors.childrenOrVulnerableAdults} />
              <YesNo label="Will an admission fee be charged? *" name="admissionFeeCharged" value={form.activity.admissionFeeCharged} onChange={(value) => updateSection("activity", "admissionFeeCharged", value)} error={errors.admissionFeeCharged} />
              <YesNo label="Are you operating commercially? *" name="commercialActivity" value={form.activity.commercialActivity} onChange={(value) => updateSection("activity", "commercialActivity", value)} error={errors.commercialActivity} />
              <YesNo label="Will food be served? *" name="foodServed" value={form.activity.foodServed} onChange={(value) => updateSection("activity", "foodServed", value)} error={errors.foodServed} />
              <YesNo label="Will music or amplified sound be used? *" name="amplifiedSound" value={form.activity.amplifiedSound} onChange={(value) => updateSection("activity", "amplifiedSound", value)} error={errors.amplifiedSound} />
              <YesNo label="Will you bring specialist equipment? *" name="specialistEquipment" value={form.activity.specialistEquipment} onChange={(value) => updateSection("activity", "specialistEquipment", value)} error={errors.specialistEquipment} />
              <Field label="Setup time required (minutes)"><input type="number" min="0" max="480" value={form.activity.setupMinutes} onChange={(e) => updateSection("activity", "setupMinutes", e.target.value)} /></Field>
              <Field label="Pack-down time required (minutes)"><input type="number" min="0" max="480" value={form.activity.packDownMinutes} onChange={(e) => updateSection("activity", "packDownMinutes", e.target.value)} /></Field>
              <Field label="Accessibility requirements" className="phx-full"><textarea rows="3" value={form.activity.accessibilityRequirements} onChange={(e) => updateSection("activity", "accessibilityRequirements", e.target.value)} maxLength="1500" /></Field>
              <Field label="Additional information" className="phx-full"><textarea rows="4" value={form.activity.additionalInformation} onChange={(e) => updateSection("activity", "additionalInformation", e.target.value)} maxLength="2000" /></Field>
            </div>
          ) : null}

          {step === 6 ? (
            <div className="phx-compliance-grid">
              <YesNo label="Do you hold public liability insurance? *" name="publicLiabilityInsurance" value={form.compliance.publicLiabilityInsurance} onChange={(value) => updateSection("compliance", "publicLiabilityInsurance", value)} error={errors.publicLiabilityInsurance} />
              <YesNo label="Does the activity need safeguarding arrangements? *" name="safeguardingRequired" value={form.compliance.safeguardingRequired} onChange={(value) => updateSafeguardingTrigger("compliance", "safeguardingRequired", value)} error={errors.safeguardingRequired} />
              {safeguardingDetailsRequired ? <>
                <div className="phx-soft-card phx-full" role="note"><ShieldCheck aria-hidden="true" /><div><h3>Safeguarding details needed</h3><p>Because children or vulnerable adults may attend, or you identified another safeguarding need, please answer the following questions. UUN will confirm any evidence required during review.</p></div></div>
                <YesNo label="Do you have a safeguarding policy? *" name="safeguardingPolicy" value={form.compliance.safeguardingPolicy} onChange={(value) => updateSection("compliance", "safeguardingPolicy", value)} error={errors.safeguardingPolicy} />
                <YesNo label="Are DBS-checked staff or volunteers required? *" name="dbsRequired" value={form.compliance.dbsRequired} onChange={(value) => updateSection("compliance", "dbsRequired", value)} error={errors.dbsRequired} />
              </> : <div className="phx-soft-card phx-full" role="note"><ShieldCheck aria-hidden="true" /><div><h3>{form.compliance.safeguardingRequired === "no" ? "No safeguarding follow-up needed" : "Safeguarding questions adapt to your activity"}</h3><p>{form.compliance.safeguardingRequired === "no" ? "These questions are hidden because you told us children or vulnerable adults will not attend and the activity has no other safeguarding need." : "Tell us whether the activity needs safeguarding arrangements. We will only ask about a policy and DBS-checked staff when those details are relevant."}</p></div></div>}
              <YesNo label="Does the activity need a licence or regulatory approval? *" name="licenceRequired" value={form.compliance.licenceRequired} onChange={(value) => updateSection("compliance", "licenceRequired", value)} error={errors.licenceRequired} />
              <YesNo label="Do you require storage? *" name="storageRequired" value={form.compliance.storageRequired} onChange={(value) => updateSection("compliance", "storageRequired", value)} error={errors.storageRequired} />
              <YesNo label="Do you require UUN staff onsite? *" name="uunStaffRequired" value={form.compliance.uunStaffRequired} onChange={(value) => updateSection("compliance", "uunStaffRequired", value)} error={errors.uunStaffRequired} />
              <div className="phx-upload-card">
                <FileUp aria-hidden="true" />
                <div><h3>Supporting documents (optional)</h3><p>Public liability insurance, safeguarding policy, risk assessment, DBS declaration, licences or programme information.</p></div>
                <input type="file" multiple accept=".pdf,.docx,.jpg,.jpeg,.png" onChange={handleFiles} disabled={!phoenixHubContent.documentUploadsEnabled} aria-describedby="upload-help" />
                <small id="upload-help">PDF, DOCX, JPG or PNG. Maximum 10 MB each and six files. Documents remain private.</small>
                {!phoenixHubContent.documentUploadsEnabled ? <p className="phx-upload-disabled">Online uploads are awaiting Firebase setup. You can still submit and UUN will request documents securely.</p> : null}
                {fileError ? <strong role="alert">{fileError}</strong> : null}
                {files.length ? <ul>{files.map((file) => <li key={`${file.name}-${file.size}`}>{file.name}</li>)}</ul> : null}
              </div>
            </div>
          ) : null}

          {step === 7 ? (
            <div className="phx-declaration">
              <PhoenixBookingReview
                form={form}
                spaces={spaces}
                files={files}
                onEditStep={(targetStep) => {
                  setStep(targetStep);
                  setErrors({});
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
              <div className="phx-estimate-card">
                <p className="phx-kicker">Indicative estimate</p>
                {estimate.available ? <><strong>£{estimate.subtotal.toFixed(2)}</strong><p>{estimate.hours.toFixed(1)} total space-hours across {estimate.sessions} session{estimate.sessions === 1 ? "" : "s"}. The administrator will confirm the final rate and any additional charges.</p></> : <><strong>Price on request</strong><p>UUN will prepare an estimate after reviewing the space, dates and activity.</p></>}
                <small>{phoenixHubContent.pricingNote}</small>
              </div>
              <div className="phx-declaration-list">
                {[
                  ["accurate", "I confirm that the information supplied is accurate."],
                  ["requestOnly", "I understand this is a booking request and not a confirmed booking."],
                  ["contactConsent", "I agree to be contacted about this request."],
                ].map(([key, label]) => <label key={key} className={errors[key] ? "phx-field-error" : ""}><input type="checkbox" checked={form.declarations[key]} onChange={(e) => updateSection("declarations", key, e.target.checked)} /><span>{label}{errors[key] ? <strong role="alert">{errors[key]}</strong> : null}</span></label>)}
                <label className={errors.privacyAccepted ? "phx-field-error" : ""}><input type="checkbox" checked={form.declarations.privacyAccepted} onChange={(e) => updateSection("declarations", "privacyAccepted", e.target.checked)} /><span>I accept the <PolicyReference href={phoenixHubContent.privacyUrl}>privacy notice</PolicyReference>.{errors.privacyAccepted ? <strong role="alert">{errors.privacyAccepted}</strong> : null}</span></label>
                <label className={errors.termsAccepted ? "phx-field-error" : ""}><input type="checkbox" checked={form.declarations.termsAccepted} onChange={(e) => updateSection("declarations", "termsAccepted", e.target.checked)} /><span>I agree to follow the <PolicyReference href={phoenixHubContent.termsUrl}>booking terms</PolicyReference>, <PolicyReference href={phoenixHubContent.cancellationUrl}>cancellation policy</PolicyReference>, <PolicyReference href={phoenixHubContent.safeguardingUrl}>safeguarding requirements</PolicyReference> and <PolicyReference href={phoenixHubContent.centreRulesUrl}>centre rules</PolicyReference> if approved.{errors.termsAccepted ? <strong role="alert">{errors.termsAccepted}</strong> : null}</span></label>
              </div>
              <label className="phx-honeypot" aria-hidden="true">Leave this field empty<input tabIndex="-1" autoComplete="off" value={form.honeypot} onChange={(e) => setForm((current) => ({ ...current, honeypot: e.target.value }))} /></label>
              <div className="phx-soft-card"><ShieldCheck aria-hidden="true" /><div><h3>Your request stays private</h3><p>Booking details, safeguarding answers and documents are only available to authorised UUN administrators.</p></div></div>
            </div>
          ) : null}
        </section>

        {submitError ? <div className="phx-submit-error" role="alert">{submitError}</div> : null}
        <div className="phx-form-actions">
          {step > 1 ? <button type="button" className="phx-button phx-button-secondary" onClick={() => { setStep((current) => current - 1); setErrors({}); }}><ArrowLeft aria-hidden="true" /> Previous</button> : <span />}
          {step < 7 ? <button type="button" className="phx-button" onClick={validateAndContinue}>Continue <ArrowRight aria-hidden="true" /></button> : <button type="submit" className="phx-button" disabled={submitting}>{submitting ? <><Loader2 className="phx-spin" aria-hidden="true" /> Sending request…</> : <>Submit booking request <ArrowRight aria-hidden="true" /></>}</button>}
        </div>
      </form>
    </div>
  );
}
